import { useEffect, useState, type FormEvent } from "react";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import Radio from "lucide-react/dist/esm/icons/radio";
import Trophy from "lucide-react/dist/esm/icons/trophy";
import type { Card, RankingResponse } from "../../game";
import { deviceKeyHash } from "../identity";
import { readStats } from "../stats";
import type { ConnectionStatus } from "../use-game";
import { PlayingCard } from "./playing-card";

interface LobbyProps {
  connection: ConnectionStatus;
  busy: boolean;
  onCreate: (name: string, decks: 1 | 2) => Promise<unknown>;
  onJoin: (code: string, name: string) => Promise<unknown>;
  onOpenRules: () => void;
}

const HERO_CARDS: Card[] = [
  { id: "hero-spades-13", rank: 13, suit: "spades" },
  { id: "hero-hearts-1", rank: 1, suit: "hearts" },
  { id: "hero-diamonds-12", rank: 12, suit: "diamonds" },
];

function storedName(): string {
  try {
    return localStorage.getItem("chin:player-name:v1") ?? "";
  } catch {
    return "";
  }
}

function invitedCode(): string {
  return new URLSearchParams(window.location.search).get("sala")?.toUpperCase() ?? "";
}

export function Lobby({ connection, busy, onCreate, onJoin, onOpenRules }: LobbyProps) {
  const initialCode = invitedCode();
  const [mode, setMode] = useState<"create" | "join" | "ranking">(initialCode ? "join" : "create");
  const [name, setName] = useState(storedName);
  const [code, setCode] = useState(initialCode);
  const [doubleDeck, setDoubleDeck] = useState(false);
  const [stats] = useState(readStats);
  const [ranking, setRanking] = useState<RankingResponse | null>(null);
  const [rankingError, setRankingError] = useState(false);
  const offline = connection !== "online";

  useEffect(function loadRanking() {
    if (mode !== "ranking" || ranking !== null) return;
    let cancelled = false;

    void (async () => {
      try {
        const me = await deviceKeyHash();
        const response = await fetch(`/api/ranking${me ? `?me=${me}` : ""}`);
        if (!response.ok) throw new Error(`ranking ${response.status}`);
        const data = (await response.json()) as RankingResponse;
        if (!cancelled) setRanking(data);
      } catch {
        if (!cancelled) setRankingError(true);
      }
    })();

    return function cancelRankingLoad() {
      cancelled = true;
    };
  }, [mode, ranking]);

  function updateName(value: string): void {
    const next = value.slice(0, 18);
    setName(next);
    try {
      localStorage.setItem("chin:player-name:v1", next);
    } catch {
      // Storage is an enhancement, never a blocker.
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (name.trim().length < 2 || busy || offline) return;
    if (mode === "create") void onCreate(name.trim(), doubleDeck ? 2 : 1);
    else if (code.trim().length >= 4) void onJoin(code.trim(), name.trim());
  }

  return (
    <main className="lobby-shell">
      <header className="lobby-nav">
        <a className="wordmark" href="/" aria-label="CHIN, inicio">CHIN!</a>
        <button className="icon-text-button" type="button" onClick={onOpenRules}>
          <BookOpen aria-hidden="true" size={19} strokeWidth={2.2} />
          <span>Cómo jugar</span>
        </button>
      </header>

      <section className="lobby-hero" aria-labelledby="hero-title">
        <div className="lobby-copy">
          <p className="eyebrow"><Radio aria-hidden="true" size={15} /> 1 vs 1 · en tiempo real</p>
          <h1 id="hero-title">Pensá rápido.<br /><em>Jugá más rápido.</em></h1>
          <p className="lobby-lead">Cartas consecutivas, reflejos y un solo grito que cambia la mesa.</p>
        </div>

        <div className="hero-cards" aria-hidden="true">
          {HERO_CARDS.map((card, index) => (
            <div className={`hero-card hero-card--${index + 1}`} key={card.id}>
              <PlayingCard card={card} size="center" />
            </div>
          ))}
          <span className="hero-burst">¡CHIN!</span>
        </div>

        <form className="lobby-panel" onSubmit={submit}>
          <div className="mode-switch" aria-label="Elegir cómo entrar">
            <button
              type="button"
              className={mode === "create" ? "is-active" : ""}
              aria-pressed={mode === "create"}
              onClick={() => setMode("create")}
            >
              Crear mesa
            </button>
            <button
              type="button"
              className={mode === "join" ? "is-active" : ""}
              aria-pressed={mode === "join"}
              onClick={() => setMode("join")}
            >
              Tengo código
            </button>
            <button
              type="button"
              className={mode === "ranking" ? "is-active" : ""}
              aria-pressed={mode === "ranking"}
              onClick={() => setMode("ranking")}
            >
              <Trophy aria-hidden="true" size={14} /> Ranking
            </button>
          </div>

          {mode === "ranking" ? (
            <div className="ranking-panel" aria-label="Ranking de jugadores">
              {rankingError ? (
                <p className="ranking-empty">No pudimos cargar el ranking. Probá más tarde.</p>
              ) : ranking === null ? (
                <p className="ranking-empty">Cargando ranking…</p>
              ) : ranking.top.length === 0 ? (
                <p className="ranking-empty">
                  Todavía no hay jugadores rankeados. Se entra con {ranking.minMatches} partidas jugadas.
                </p>
              ) : (
                <>
                  <ol className="ranking-list">
                    {ranking.top.map((player) => (
                      <li
                        key={`${player.position}-${player.name}`}
                        className={ranking.me?.position === player.position ? "is-me" : ""}
                      >
                        <span className="ranking-pos">{player.position}</span>
                        <span className="ranking-name">{player.name}</span>
                        <span className="ranking-record">{player.wins}–{player.losses}</span>
                        <b className="ranking-rating">{player.rating}</b>
                      </li>
                    ))}
                  </ol>
                  {ranking.me && ranking.me.position > ranking.top.length ? (
                    <div className="ranking-me">
                      <span className="ranking-pos">{ranking.me.position}</span>
                      <span className="ranking-name">{ranking.me.name} (vos)</span>
                      <span className="ranking-record">{ranking.me.wins}–{ranking.me.losses}</span>
                      <b className="ranking-rating">{ranking.me.rating}</b>
                    </div>
                  ) : null}
                  {ranking.me && ranking.me.position === 0 ? (
                    <p className="ranking-empty">
                      Te faltan {Math.max(0, ranking.minMatches - ranking.me.matches)} partidas para entrar al ranking.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : (
            <>
          <label className="field-label" htmlFor="player-name">Tu nombre</label>
          <input
            id="player-name"
            className="text-field"
            value={name}
            onChange={(event) => updateName(event.target.value)}
            placeholder="Ej. Mica"
            autoComplete="nickname"
            enterKeyHint={mode === "create" ? "go" : "next"}
            maxLength={18}
            required
          />

          {mode === "create" ? (
            <label className="deck-toggle">
              <input
                type="checkbox"
                checked={doubleDeck}
                onChange={(event) => setDoubleDeck(event.target.checked)}
              />
              <span>
                <strong>Partida larga: 2 mazos</strong>
                <small>104 cartas en juego, ideal para series</small>
              </span>
            </label>
          ) : null}

          {mode === "join" ? (
            <>
              <label className="field-label" htmlFor="room-code">Código de mesa</label>
              <input
                id="room-code"
                className="text-field text-field--code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase().slice(0, 6))}
                placeholder="ABC123"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                maxLength={6}
                required
              />
            </>
          ) : null}

          <button
            className="primary-button"
            type="submit"
            disabled={busy || offline || name.trim().length < 2 || (mode === "join" && code.length < 4)}
          >
            {busy ? "Preparando…" : offline ? "Conectando…" : mode === "create" ? "Crear partida" : "Entrar a jugar"}
          </button>

          <p className="form-hint">Sin registro · baraja de póker · ideal con sonido</p>
            </>
          )}
        </form>
      </section>

      {stats.matches > 0 ? (
        <section className="lobby-stats" aria-label="Tus estadísticas">
          <span><b>{stats.wins}</b> victorias</span>
          <span><b>{stats.matches}</b> partidas</span>
          <span><b>{stats.chins}</b> CHIN</span>
          <span><b>{stats.bestStreak}</b> mejor racha</span>
        </section>
      ) : null}

      <footer className="lobby-footer">
        <span className={`connection-dot connection-dot--${connection}`} />
        {connection === "online" ? "Servidor listo" : "Buscando servidor"}
        <span aria-hidden="true">·</span>
        <span>Hecho para tocar, no esperar</span>
      </footer>
    </main>
  );
}
